import type {
  ParsedAIPlannerMessage,
  RecommendationsResponse,
  StreamingMealItem,
  MealPlan,
  AIPlannerPreferences,
  WeekDay,
} from '@/store/ai-planner';

/**
 * Parse AI planner bot messages from streaming response
 * Handles event types: answer, recommendations, json
 */
export function parseAIPlannerMessages(
  parts: { type: string; text?: string }[]
): ParsedAIPlannerMessage {
  const result: ParsedAIPlannerMessage = {
    answer: '',
    recommendations: [],
    streamingMeals: [],
  };

  const recommendationsMap = new Map<string, string>();
  const combined = joinStreamedParts(parts);

  processStreamLines(combined, result, recommendationsMap);
  parseAccumulatedRecommendations(recommendationsMap, result);

  result.answer = normalizeText(result.answer);
  result.streamingMeals = convertRecommendationsToMeals(result.recommendations);

  return result;
}

/**
 * Join all streamed parts into a single string
 */
function joinStreamedParts(parts: { type: string; text?: string }[]): string {
  let combined = '';
  for (const part of parts) {
    if (part.type === 'text' && part.text) {
      combined += part.text;
    }
  }
  return combined;
}

/**
 * Process streaming lines and update result
 */
function processStreamLines(
  combined: string,
  result: ParsedAIPlannerMessage,
  recommendationsMap: Map<string, string>
): void {
  const lines = combined.split('\n');
  let currentEventType: string | null = null;

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEventType = line.slice(7).trim();
    } else if (line.startsWith('data: ') && currentEventType) {
      const rawData = line.slice(6);
      processEventData(currentEventType, rawData, result, recommendationsMap);
    }
  }
}

/**
 * Process individual event data
 */
function processEventData(
  eventType: string,
  rawData: string,
  result: ParsedAIPlannerMessage,
  recommendationsMap: Map<string, string>
): void {
  try {
    if (eventType === 'answer') {
      result.answer += rawData;
    } else if (eventType === 'recommendations') {
      const prev = recommendationsMap.get(eventType) ?? '';
      recommendationsMap.set(eventType, prev + rawData);
    } else if (eventType === 'json') {
      const parsed = JSON.parse(rawData);
      if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray(parsed.recommendations)
      ) {
        result.recommendations = parsed.recommendations;
      }
    }
  } catch (e) {
    console.warn(
      'Failed to parse AI planner message',
      { eventType, rawData },
      e
    );
  }
}

/**
 * Parse accumulated recommendations
 */
function parseAccumulatedRecommendations(
  recommendationsMap: Map<string, string>,
  result: ParsedAIPlannerMessage
): void {
  for (const [key, value] of Array.from(recommendationsMap.entries())) {
    if (key === 'recommendations' && value) {
      try {
        const parsed = JSON.parse(value);
        if (
          parsed &&
          typeof parsed === 'object' &&
          Array.isArray(parsed.recommendations)
        ) {
          result.recommendations = parsed.recommendations;
        }
      } catch (e) {
        console.warn('Failed to parse recommendations JSON', e);
      }
    }
  }
}

/**
 * Normalize text by removing extra spaces and fixing punctuation
 */
export function normalizeText(text: string): string {
  return text
    .replace(/\s+/g, ' ') // normalize multiple spaces
    .replace(/ ?([,.!?])/g, '$1') // remove space before punctuation
    .trim();
}

/**
 * Convert recommendations response to StreamingMealItem array
 */
export function convertRecommendationsToMeals(
  recommendations: RecommendationsResponse
): StreamingMealItem[] {
  const meals: StreamingMealItem[] = [];

  // Recommendations is now an array
  if (!Array.isArray(recommendations)) {
    console.warn('Recommendations is not an array:', recommendations);
    return meals;
  }

  for (const item of recommendations) {
    // Validate item has required fields
    if (!item?.name || !item?.date) {
      console.warn('Invalid meal item:', item);
      continue;
    }

    meals.push({
      date: item.date,
      slot: item.slot,
      name: item.name,
      image_thumb_urls: item.image_thumb_urls ?? '',
      estimated_price: item.estimated_price ?? 0,
      category_solo_meal: item.category_solo_meal ?? [],
      overall_dish_tag: item.overall_dish_tag ?? '',
      planning_profile: item.planning_profile,
    });
  }

  return meals;
}

/**
 * Convert streaming meals to MealPlan structure
 */
export function convertStreamingMealsToPlan(
  meals: StreamingMealItem[],
  preferences: Partial<AIPlannerPreferences>
): MealPlan {
  // Group meals by date
  const mealsByDate = meals.reduce(
    (acc, meal) => {
      const date = meal.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(meal);
      return acc;
    },
    {} as Record<string, StreamingMealItem[]>
  );

  // Get weekday from date
  const getWeekDay = (dateStr: string): WeekDay => {
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();
    const dayMap: Record<number, WeekDay> = {
      1: 'T2',
      2: 'T3',
      3: 'T4',
      4: 'T5',
      5: 'T6',
    };
    return dayMap[dayOfWeek] || 'T2';
  };

  const days = Object.entries(mealsByDate)
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([date, dateMeals]) => ({
      date,
      dayOfWeek: getWeekDay(date),
      meals: dateMeals.map((meal) => ({
        id: `meal-${meal.name}-${date}`,
        name: meal.name,
        deliveryTime: date,
        imageUrl: meal.image_thumb_urls,
        status: 'upcoming' as const,
        overallDishTag: meal.overall_dish_tag,
        estimatedPrice: meal.estimated_price,
        slot: meal.slot,
        user_note: meal.planning_profile?.user_note,
        budget: meal.planning_profile?.budget,
      })),
    }));

  return {
    id: `plan-${Date.now()}`,
    createdAt: new Date().toISOString(),
    preferences: preferences as AIPlannerPreferences,
    weeks: [
      {
        weekNumber: 1,
        startDate: preferences.startDate ?? new Date().toISOString(),
        endDate: preferences.endDate ?? new Date().toISOString(),
        days,
      },
    ],
  };
}
